/**
 * # Select Page from Paginated Data
 * @param  {String} page_number The page number which you want to select
 * @param  {{
*  [key:String]: {
  * indexes: Map<string, string>
  * data: [{
  * x: string,
  * y: [start_time:number, end_time:number],
  * data: {
  *  [key:string]: any
  * }
  * }]
  * }
  * }} pages The paginated chart data
  * @param  {string[]} filterIndexes The indexes to filter the data by
  * @param  {{[key: string]: any}} filterData The key-value pairs to filter the data by
  * @returns {[{
  * x: string,
  * y: [start_time:number, end_time:number],
  * data: {
  *  [key:string]: any
  * }
  * }]} The selected and filtered page data, or the selected page data if no filters are provided
  */

export const page_data_selector = (
  pages,
  page_number,
  filterIndexes = [],
  filterData = {}
) => {
  // Validate input parameters
  if (
    typeof page_number !== 'string' ||
    !pages ||
    typeof pages !== 'object' ||
    !filterData ||
    typeof filterData !== 'object'
  ) {
    throw new Error('Invalid input parameters');
  }

  // Check if the specified page number exists in the paginated data
  if (!pages.hasOwnProperty(page_number)) {
    throw new Error('Page number does not exist');
  }

  const page = pages[page_number];

  // Validate page structure
  if (
    !page ||
    !page.indexes ||
    !(page.indexes instanceof Map) ||
    !page.data ||
    !Array.isArray(page.data)
  ) {
    throw new Error('Invalid page structure');
  }

  let filteredData = page.data;

  // Filter the data based on the provided indexes
  if (filterIndexes.length > 0) {
    // Create a Set of filterIndexes for efficient lookup
    const filterSet = new Set(filterIndexes);

    filteredData = filteredData.filter(item => {
      const itemIndex = page.indexes.get(item.x);
      return filterSet.has(itemIndex);
    });
  }

  // Filter the data based on the provided key-value pairs
  if (Object.keys(filterData).length > 0) {
    filteredData = filteredData.filter(item => {
      for (const key in filterData) {
        if (item.data.hasOwnProperty(key) && item.data[key] === filterData[key]) {
          return true;
        }
      }
      return false;
    });
  }

  // Return the filtered data if filters are applied, otherwise return the selected page data
  return filteredData.length > 0 ? { data: filteredData } : { data: page.data };
};
